import React from 'react';
import { BookOpenIcon, CreditCardIcon, LockKeyholeIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { CTAButton } from './Shared';
import { HerbSprig, LeafCluster, Mandala, MortarPestle } from './HealingIcons';
const productMockupUrl = 'https://cdn.magicpatterns.com/uploads/mWTuvE47PoqkYMryatpyWz/Mockup_Botiquin_Ancestral.png';
export function Hero() {
  const reduceMotion = useReducedMotion();
  return <header className="relative isolate overflow-hidden pb-8 pt-3 sm:pt-7 md:pb-14 md:pt-16" data-id="element-124" data-mp="xa4h5bx7i9f2">
      <Mandala className="pointer-events-none absolute -right-20 top-4 -z-10 h-72 w-72 opacity-[0.11] md:right-6 md:h-96 md:w-96" color="#B88A35" data-id="element-125" data-mp="sart4nulmwaw" />
      <Mandala className="pointer-events-none absolute -bottom-24 -left-24 -z-10 h-80 w-80 opacity-[0.09] md:h-[30rem] md:w-[30rem]" color="#2E5946" data-id="element-126" data-mp="nekqp51ew2xp" />
      <HerbSprig className="absolute left-[7%] top-48 hidden h-14 w-14 opacity-35 md:block" color="#647D56" data-id="element-127" data-mp="3814alm6t1smr" />
      <LeafCluster className="absolute right-[7%] top-56 hidden h-16 w-16 opacity-30 md:block" color="#2E5946" data-id="element-128" data-mp="3o7lqjmmw8ymu" />

      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-129" data-mp="399ba3kjqp7ue">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-130" data-mp="3e963pvuwgbj5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-brand-gold/55 bg-brand-paper px-3 py-1 shadow-sm sm:mb-5 sm:px-4 sm:py-1.5" data-id="element-131" data-mp="2viy1rh2xrr4j">
            <HerbSprig className="h-3.5 w-3.5 sm:h-4 sm:w-4" color="#2E5946" data-id="element-132" data-mp="2hjoux80me9d8" />
            <span className="font-serif text-[10px] font-semibold tracking-[0.16em] text-brand-ink sm:text-xs" data-id="element-133" data-mp="2ljpef72ktar5">
              LUMEN AKASHA™
            </span>
          </div>

          <p className="mb-1 font-serif font-semibold uppercase tracking-[0.16em] text-brand-forest sm:mb-3 sm:text-base sm:tracking-[0.18em] md:text-lg text-[18px]" data-id="element-134" data-mp="nat65811aej4">
            El Botiquín Ancestral™
          </p>
          <h1 className="mx-auto max-w-5xl font-display font-bold text-brand-ink sm:text-5xl sm:leading-[0.94] md:text-7xl text-[35px]" data-id="element-135" data-mp="de6oes4083lb">
            <span className="block" data-id="element-136" data-mp="1ayt9d7fj7xel">TU GUÍA VISUAL DE</span>
            <span className="relative z-0 mt-0.5 inline-block px-1.5 text-brand-purpleDark sm:mt-1 sm:px-3" data-id="element-137" data-mp="162uf537ivl69">
              <span className="absolute inset-x-0 bottom-[0.04em] -z-10 h-[0.48em] -rotate-1 rounded-sm bg-brand-goldLight/90" aria-hidden="true" data-id="element-138" data-mp="111es55lsb4l8" />
              DIGITOPUNTURA
            </span>
            <span className="block" data-id="element-139" data-mp="3lqm2jypo95ux">PARA EL ALIVIO INMEDIATO</span>
          </h1>
          <p className="mx-auto mt-2 max-w-xs font-serif font-semibold text-brand-forest sm:hidden text-[20px]" data-id="element-140" data-mp="3hq266xcxa69u">
            50 puntos esenciales para aliviar los síntomas más frecuentes.
          </p>
          <p className="mx-auto mt-2 hidden max-w-3xl text-base leading-relaxed text-brand-text sm:block md:mt-5 md:text-xl" data-id="element-141" data-mp="21b66k5ekreqc">
            El conocimiento ancestral ya existe. La herramienta visual para
            aplicarlo en segundos, ahora también.
          </p>

          <div className="relative mx-auto mt-3 w-full max-w-[25rem] sm:mt-8 sm:max-w-3xl" data-id="element-142" data-mp="2fyr7ptrw33vm">
            <MortarPestle className="absolute -left-5 top-1/2 hidden h-12 w-12 -translate-y-1/2 opacity-50 md:block" color="#B66735" data-id="element-143" data-mp="2kxubzc01p62j" />
            <motion.img src={productMockupUrl} alt="Mockup de El Botiquín Ancestral con ebook, fichas visuales y seis bonos de regalo" initial={reduceMotion ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: reduceMotion ? 0 : 0.12,
            ease: [0.22, 1, 0.36, 1]
          }} className="mx-auto h-auto max-h-[28svh] w-full object-contain drop-shadow-[0_18px_22px_rgba(34,54,70,0.22)] sm:max-h-[36rem] sm:max-w-2xl md:max-w-3xl" draggable={false} decoding="async" data-id="element-144" data-mp="2tzbo6jac0nly" />
          </div>

          <div className="mt-2 flex justify-center sm:hidden" data-id="element-145" data-mp="3rg3mxw68f68e">
            <CTAButton className="w-full max-w-[22rem] px-5 py-3 text-[13px] leading-tight" data-id="element-146" data-mp="3rhep5rk145xe">
              → QUIERO MI BOTIQUÍN ANCESTRAL AHORA
            </CTAButton>
          </div>

          <div className="mx-auto mt-8 hidden max-w-3xl space-y-3 px-2 sm:block" data-id="element-147" data-mp="2yyhpdkpfu65x">
            <h2 className="font-serif text-xl font-bold uppercase tracking-wide text-brand-forest md:text-3xl" data-id="element-148" data-mp="2tzn0c10vnldd">
              EL BOTIQUÍN ANCESTRAL™
            </h2>
            <p className="font-serif text-base font-semibold text-brand-text md:text-xl" data-id="element-149" data-mp="1e93x7p0wwink">
              50 puntos esenciales para aliviar los síntomas más frecuentes.
            </p>
            <p className="text-sm text-brand-muted md:text-base" data-id="element-150" data-mp="zimyidfnw6ud">
              Tu guía visual de digitopuntura para el alivio inmediato.
            </p>
          </div>

          <div className="mt-7 hidden flex-col items-center gap-5 sm:flex" data-id="element-151" data-mp="1s9ofmk2x0cgj">
            <CTAButton className="px-7 py-4 text-sm md:px-10 md:text-lg" data-id="element-152" data-mp="1x76rai8bh5s8">
              → QUIERO MI BOTIQUÍN ANCESTRAL AHORA
            </CTAButton>
            <div className="flex max-w-3xl flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-brand-muted md:text-sm" data-id="element-153" data-mp="1jat9f7zvf2d8">
              <span className="inline-flex items-center gap-1.5" data-id="element-154" data-mp="7tjr4oshagdi">
                <ShieldCheckIcon className="h-4 w-4 text-brand-forest" data-id="element-155" data-mp="7vegqgep2wnx" />
                Garantía 7 días
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-156" data-mp="2ehapnnlgei6f">
                <BookOpenIcon className="h-4 w-4 text-brand-forest" data-id="element-157" data-mp="2egsnkac419ql" />
                Listo para usar en tablet o imprimir
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-158" data-mp="24r8z8p5monoc">
                <LockKeyholeIcon className="h-4 w-4 text-brand-forest" data-id="element-159" data-mp="24peebaucwvk9" />
                Pago seguro
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-160" data-mp="1vqt0fszty4pt">
                <CreditCardIcon className="h-4 w-4 text-brand-forest" data-id="element-161" data-mp="1vozqa3q0d2d2" />
                Mercado Pago
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold text-brand-ink md:text-sm" data-id="element-162" data-mp="1lsd6zjdbyjd5">
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-163" data-mp="1gulikzbw9fdp">
                <BookOpenIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-164" data-mp="3g75yb676knza" />
                100% PDF
              </span>
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-165" data-mp="3b8mdxa88jk91">
                <ZapIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-166" data-mp="3joldn9zzygx5" />
                Acceso en 5 min
              </span>
              <span className="rounded-full border border-brand-green/30 bg-brand-paper px-3 py-2" data-id="element-167" data-mp="3sh5dgds0yrjk">
                <SparklesIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-168" data-mp="1aui8y9p4l05" />
                Para todos los niveles
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </header>;
}